// =============================================
// MusicPlaylists — create/edit playlists and play them
// =============================================
// Two views: a list of the user's playlists (create / open / delete) and a detail
// view (ordered songs with play / reorder / remove + an "add songs" picker drawn
// from the user's owned tracks). Backed by mus_playlist + mus_playlist_song via
// musicLibrary; reloads the tables after writes so it's correct in API + local modes.
// =============================================
import React, { useCallback, useEffect, useState } from 'react';
import { FiArrowLeft, FiPlay, FiTrash2, FiPlus, FiChevronUp, FiChevronDown, FiList, FiX, FiCheck } from 'react-icons/fi';
import {
  TrackView, PlaylistView, PlaylistSongView,
  reloadPlaylistTables, getPlaylists, getPlaylistSongs, createPlaylist, deletePlaylist,
  addTrackToPlaylist, removeFromPlaylist, movePlaylistSong,
} from './musicLibrary';
import MusicCover from './MusicCover';

interface Props {
  userId: number;
  allTracks: TrackView[];
  onBack: () => void;
  onPlay: (tracks: TrackView[], index: number, title?: string) => void;
  currentTrackId: number | null;
}

function fmtDuration(sec: number): string {
  if (!sec) return '—';
  const m = Math.floor(sec / 60), s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export const MusicPlaylists: React.FC<Props> = ({ userId, allTracks, onBack, onPlay, currentTrackId }) => {
  const [playlists, setPlaylists] = useState<PlaylistView[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [songs, setSongs] = useState<PlaylistSongView[]>([]);
  const [newName, setNewName] = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async (keepSelected = selectedId) => {
    await reloadPlaylistTables();
    setPlaylists(getPlaylists(userId));
    if (keepSelected != null) setSongs(getPlaylistSongs(keepSelected, userId));
  }, [userId, selectedId]);

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selected = playlists.find(p => p.playlistId === selectedId) || null;

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    const id = await createPlaylist(userId, newName);
    setNewName('');
    await reload(id);
    setBusy(false);
    setSelectedId(id);
  };

  const open = async (id: number) => { setSelectedId(id); setSongs(getPlaylistSongs(id, userId)); };

  const del = async (id: number, name: string) => {
    if (!window.confirm(`Delete playlist "${name}"? (Your songs stay in your library.)`)) return;
    setBusy(true);
    await deletePlaylist(id);
    setSelectedId(null);
    await reload(null);
    setBusy(false);
  };

  const addSong = async (trackId: number) => { if (selectedId == null) return; await addTrackToPlaylist(selectedId, trackId, userId); await reload(selectedId); };
  const removeSong = async (psId: number) => { await removeFromPlaylist(psId); await reload(selectedId); };
  const move = async (psId: number, dir: 'up' | 'down') => { if (selectedId == null) return; await movePlaylistSong(selectedId, psId, dir, userId); await reload(selectedId); };

  // ---------- List view ----------
  if (selectedId == null) {
    return (
      <div className="mcv">
        <div className="mcv-head">
          <button className="mcv-back" onClick={onBack}><FiArrowLeft /></button>
          <div><h3 className="mcv-title">Your Playlists</h3><p className="mcv-subtitle">{playlists.length} playlist{playlists.length === 1 ? '' : 's'}</p></div>
        </div>

        <div className="pl-create">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="New playlist name"
            onKeyDown={e => { if (e.key === 'Enter') create(); }} />
          <button className="mlg-btn mlg-btn-primary" onClick={create} disabled={busy || !newName.trim()}><FiPlus /> Create</button>
        </div>

        {playlists.length === 0 ? (
          <p className="music-empty">No playlists yet. Create one above.</p>
        ) : (
          <div className="pl-list">
            {playlists.map(p => (
              <div key={p.playlistId} className="pl-card" onClick={() => open(p.playlistId)}>
                <FiList className="pl-card-icon" />
                <div className="pl-card-main">
                  <span className="pl-card-name">{p.name}</span>
                  <span className="pl-card-count">{p.songCount} song{p.songCount === 1 ? '' : 's'}</span>
                </div>
                <button className="pl-card-del" onClick={(e) => { e.stopPropagation(); del(p.playlistId, p.name); }} title="Delete playlist"><FiTrash2 /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ---------- Detail view ----------
  const inPlaylist = new Set(songs.map(s => s.track.trackId));
  const candidates = allTracks.filter(t => !inPlaylist.has(t.trackId));

  return (
    <div className="mcv">
      <div className="mcv-head">
        <button className="mcv-back" onClick={() => { setSelectedId(null); setShowPicker(false); }}><FiArrowLeft /></button>
        <div><h3 className="mcv-title">{selected?.name}</h3><p className="mcv-subtitle">{songs.length} song{songs.length === 1 ? '' : 's'}</p></div>
        {songs.length > 0 && <button className="mcv-playall" onClick={() => onPlay(songs.map(s => s.track), 0, selected?.name || 'Playlist')}><FiPlay /> Play all</button>}
      </div>

      <div className="pl-detail-actions">
        <button className="mlg-btn mlg-btn-secondary" onClick={() => setShowPicker(s => !s)}>
          {showPicker ? <><FiCheck /> Done adding</> : <><FiPlus /> Add songs</>}
        </button>
      </div>

      {showPicker && (
        <div className="pl-picker">
          {candidates.length === 0 ? (
            <p className="music-empty">All your songs are already in this playlist.</p>
          ) : candidates.map(t => (
            <div key={t.trackId} className="pl-pick-row">
              <div className="pl-pick-main"><span className="pl-pick-title">{t.title}</span><span className="pl-pick-sub">{t.artist}</span></div>
              <button className="pl-pick-add" onClick={() => addSong(t.trackId)} title="Add"><FiPlus /></button>
            </div>
          ))}
        </div>
      )}

      {songs.length === 0 ? (
        <p className="music-empty">This playlist is empty. Use “Add songs”.</p>
      ) : (
        <div className="music-list">
          {songs.map((s, i) => (
            <div key={s.playlistSongId} className={`music-row ${currentTrackId === s.track.trackId ? 'playing' : ''}`}>
              <button className="music-row-play" onClick={() => onPlay(songs.map(x => x.track), i, selected?.name || 'Playlist')} title="Play"><FiPlay /></button>
              <span className="music-row-num">{i + 1}</span>
              <MusicCover coverKey={s.track.coverKey} size={38} />
              <div className="music-row-main">
                <span className="music-row-title">{s.track.title}</span>
                <span className="music-row-sub">{s.track.artist}{s.track.album ? ` · ${s.track.album}` : ''}</span>
              </div>
              <span className="music-row-dur">{fmtDuration(s.track.durationSeconds)}</span>
              <button className="music-row-icon" onClick={() => move(s.playlistSongId, 'up')} disabled={i === 0} title="Move up"><FiChevronUp /></button>
              <button className="music-row-icon" onClick={() => move(s.playlistSongId, 'down')} disabled={i === songs.length - 1} title="Move down"><FiChevronDown /></button>
              <button className="music-row-icon music-row-del" onClick={() => removeSong(s.playlistSongId)} title="Remove from playlist"><FiX /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MusicPlaylists;
